// メニュー項目の位置。 ARENA 座標 (960x540) で返す。
//   render.ts が描画位置として、 game.ts が当たり判定として、 同じ定数・同じ関数から座標を取る唯一の出所。
//   片方だけ直すと見た目と押せる場所がズレる事故を防ぐため、 座標の生の数値はここだけに置く
//   (render.ts は自前の定数を持たず、 このファイルからインポートして使う)。

import { ARENA } from './balance'
import { titleV9, TITLE_V9_MENU_ROW_H, TITLE_V9_MENU_TOP, TITLE_V9_MENU_W } from './title_v9'

export type MenuRect = { x: number, y: number, w: number, h: number }

const cx = ARENA.width / 2

// ----- タイトルの縦メニュー (始める/常夜燈の稽古/幕間の記録) -----
export const TITLE_MENU_LEFT = 96
export const TITLE_MENU_TOP = 372
export const TITLE_MENU_ROW_H = 42
export const TITLE_MENU_TEXT_OFFSET_X = 16   // テキストは menuLeft + この分だけ右から
export const TITLE_MENU_FONT_PX = 20

// ----- 「幕を選ぶ」(難易度選択) の2択 -----
export const DIFFICULTY_LEFT_NAME = 116
export const DIFFICULTY_START_Y = 288
export const DIFFICULTY_NAME_ROW_H = 30
export const DIFFICULTY_NAME_FONT_PX = 30
export const DIFFICULTY_DESC_LINE_H = 25
export const DIFFICULTY_DESC_GAP = 6
export const DIFFICULTY_TAIL_GAP = 20
// 千秋楽=3行 / 初演=4行。 実データは render.ts の DIFFICULTY_OPTIONS[i].desc。
//   ⚠️ ここだけは実データの複製なので、 説明文の行数を変えるとこの値が古くなり、
//   「幕を選ぶ」の当たり判定が黙ってズレる (2番目の項目のY座標が説明の行数に依存するため)。
//   tests/menu_layout.test.ts が実データとの一致を機械で固定している。 数字を変えるならテストも見ること。
export const DIFFICULTY_DESC_LINE_COUNTS = [3, 4]

// ----- 幕間メインメニュー -----
export const PAUSE_MAIN_PANEL_W = 260
export const PAUSE_MAIN_PANEL_Y = 130
export const PAUSE_MAIN_ROW_H = 34
export const PAUSE_MAIN_FIRST_ROW_OFFSET_Y = 70   // my = py + この分
export const PAUSE_MAIN_FONT_PX = 15

// ----- 音と揺れ設定パネル -----
export const PAUSE_SETTINGS_PANEL_W = 340
export const PAUSE_SETTINGS_PANEL_Y = 160
export const PAUSE_SETTINGS_ROW_H = 30
export const PAUSE_SETTINGS_FIRST_ROW_OFFSET_Y = 68   // sy = py + この分
export const PAUSE_SETTINGS_FONT_PX = 15

// ----- 終幕の選択 (城に残る/城を出る・横並び) -----
export const ENDING_CHOICE_OFFSET_X = 150
export const ENDING_CHOICE_Y_FRAC = 0.62
export const ENDING_CHOICE_FONT_PX = 34

// fillText のベースライン座標から、当たり矩形の上端Yを逆算する目安。
//   キャップハイトはフォントサイズの概ね 0.75〜0.8 倍。
function rectFromBaseline(centerOrLeftX: number, align: 'left' | 'center', baselineY: number, fontSizePx: number, width: number): MenuRect {
  const top = baselineY - fontSizePx * 0.78
  const h = fontSizePx * 1.35
  const x = align === 'center' ? centerOrLeftX - width / 2 : centerOrLeftX
  return { x, y: top, w: width, h }
}

// 一定間隔 (rowH) で並ぶ項目向け。 グリフの実測ではなく行の高さいっぱいを当たり判定にする
//   (行と行の間に押せない隙間を作らない・一般的なリスト/メニューUIのクリック領域と同じ考え方)。
//   baselineY はその行の描画に使う fillText の y。 行の下 1/3 あたりに baseline が来る配分。
function rectFromRowSpan(centerOrLeftX: number, align: 'left' | 'center', baselineY: number, rowH: number, width: number): MenuRect {
  const top = baselineY - rowH * (2 / 3)
  const x = align === 'center' ? centerOrLeftX - width / 2 : centerOrLeftX
  // 隣接する行とちょうど rowH で並ぶため、浮動小数の丸め誤差で境界がわずかに重なることがある。
  //   見た目・押しやすさに影響しない程度 (0.5px) だけ詰めて、確実に重ならないようにする。
  return { x, y: top, w: width, h: rowH - 0.5 }
}

// タイトルの縦メニューの項目 (2026-10-19)。 描画 (render.ts)・当たり判定と決定 (game.ts) の唯一の出所。
//   進み具合が保存されている時だけ「続きから」を先頭に足す = 5つ。 無い時は「最初から」から始まる4つ。
//   最後は「クレジット」(2026-10-31 作者「タイトルのメニューに足す」)。
//   決定の分岐は index ではなく項目の名前で行う (数が変わっても押した物と動く物がずれない)。
export type TitleMenuItem = 'continue' | 'newGame' | 'practice' | 'records' | 'credits'
export const TITLE_MENU_LABELS: Record<TitleMenuItem, string> = {
  continue: '続きから',
  newGame: '最初から',
  practice: '常夜燈の稽古',
  records: '幕間の記録',
  credits: 'クレジット',
}
export function titleMenuItems(hasProgress: boolean): TitleMenuItem[] {
  return hasProgress
    ? ['continue', 'newGame', 'practice', 'records', 'credits']
    : ['newGame', 'practice', 'records', 'credits']
}

// タイトルの縦メニュー (src/render.ts drawTitleScreen 参照)。
//   当たり矩形は左の灯の点 (menuLeft) を含む幅にする。 count = titleMenuItems(...).length。
// 一番上の行の位置。 4行までは TITLE_MENU_TOP から下へ、 5行目からは下端 (4行の時の最後の行) を保って上へ伸ばす。
export function titleMenuTop(count: number): number {
  return TITLE_MENU_TOP - Math.max(0, count - 4) * TITLE_MENU_ROW_H
}

export function titleMenuRects(count = 4): MenuRect[] {
  const out: MenuRect[] = []
  if (titleV9()) {
    // 比較版 ?title=v9 (src/title_v9.ts): ロゴの下の中央に縦に並ぶ。
    for (let i = 0; i < count; i += 1) {
      out.push(rectFromRowSpan(cx, 'center', TITLE_V9_MENU_TOP + i * TITLE_V9_MENU_ROW_H, TITLE_V9_MENU_ROW_H, TITLE_V9_MENU_W))
    }
    return out
  }
  const top = titleMenuTop(count)
  for (let i = 0; i < count; i += 1) {
    const y = top + i * TITLE_MENU_ROW_H
    out.push(rectFromRowSpan(TITLE_MENU_LEFT - 14, 'left', y, TITLE_MENU_ROW_H, 240))
  }
  return out
}

// ----- 「最初から始める」の確かめ (2026-10-19) -----
//   「幕を選ぶ」と同じ左寄せ・箱なしの並び (タイトルの絵を隠さない)。
export const NEW_GAME_CONFIRM_LEFT = 116
export const NEW_GAME_CONFIRM_EYEBROW_Y = 246
export const NEW_GAME_CONFIRM_BODY_Y = 290
export const NEW_GAME_CONFIRM_BODY_LINE_H = 25
export const NEW_GAME_CONFIRM_CHOICE_Y = 380
export const NEW_GAME_CONFIRM_ROW_H = 40
export const NEW_GAME_CONFIRM_FONT_PX = 24
export const NEW_GAME_CONFIRM_ITEMS = ['始める', 'やめる'] as const

export function newGameConfirmRects(): MenuRect[] {
  const out: MenuRect[] = []
  for (let i = 0; i < NEW_GAME_CONFIRM_ITEMS.length; i += 1) {
    const y = NEW_GAME_CONFIRM_CHOICE_Y + i * NEW_GAME_CONFIRM_ROW_H
    out.push(rectFromRowSpan(NEW_GAME_CONFIRM_LEFT - 20, 'left', y, NEW_GAME_CONFIRM_ROW_H, 240))
  }
  return out
}

// 「幕を選ぶ」(難易度選択) の2択 (src/render.ts drawDifficultyOptions 参照)。
//   ⚠️ 選択中の項目だけ説明文が付いて行送りが可変になるため、 2番目以降の項目のY座標は
//   「今どちらが選ばれているか」に依存する。 render.ts と game.ts が同じ selectedIndex を
//   渡す限り、描画位置と当たり矩形は必ず一致する (どちらか一方だけ直しても壊れない)。
export function difficultyMenuRects(selectedIndex: number): MenuRect[] {
  const out: MenuRect[] = []
  let y = DIFFICULTY_START_Y
  for (let i = 0; i < 2; i += 1) {
    out.push(rectFromBaseline(DIFFICULTY_LEFT_NAME - 20, 'left', y, DIFFICULTY_NAME_FONT_PX, 320))
    y += DIFFICULTY_NAME_ROW_H
    if (i === selectedIndex) {
      y += DIFFICULTY_DESC_GAP + DIFFICULTY_DESC_LINE_COUNTS[i]! * DIFFICULTY_DESC_LINE_H
    }
    y += DIFFICULTY_TAIL_GAP
  }
  return out
}

// 幕間メニューのメイン項目。 描画 (render.ts) と 当たり判定・確定 (game.ts) の唯一の出所。
//   一戦だけモード (?fight=・2026-07-28) は本編への入口を出さない:
//     「常夜燈の稽古」→「はじめから」(同じボスをもう一度) へ差し替え、
//     「タイトルへ戻る」は落とす (タイトルへ出せば、 渡したリンクから本編が丸ごと遊べてしまう)。
export function pauseMainItems(solo: boolean): string[] {
  return solo
    ? ['幕へ戻る', 'はじめから', '幕間の記録', '音と揺れ']
    : ['幕へ戻る', '常夜燈の稽古', '幕間の記録', '音と揺れ', 'タイトルへ戻る']
}

// 幕間メインメニュー (src/render.ts drawPauseOverlay 参照)。
//   count = 項目数。 通常は5、 一戦だけモード (?fight=) は「タイトルへ戻る」が無いので4
//   (2026-07-28)。 描画と当たり判定が同じ数を見るよう、 呼び出し側は PAUSE_MAIN_COUNT 経由で渡す。
export function pauseMainRects(count = 5): MenuRect[] {
  const my0 = PAUSE_MAIN_PANEL_Y + PAUSE_MAIN_FIRST_ROW_OFFSET_Y
  const out: MenuRect[] = []
  for (let i = 0; i < count; i += 1) {
    out.push(rectFromRowSpan(cx, 'center', my0 + i * PAUSE_MAIN_ROW_H, PAUSE_MAIN_ROW_H, PAUSE_MAIN_PANEL_W - 32))
  }
  return out
}

// 音と揺れ設定パネル (src/render.ts drawSettingsPanel 参照)。
//   ラベル(cx-16 右揃え)と値(cx+20 左揃え)を両方含む1行の矩形にする (どちらをクリックしてもトグルできる)。
export function pauseSettingsRects(): MenuRect[] {
  const sy0 = PAUSE_SETTINGS_PANEL_Y + PAUSE_SETTINGS_FIRST_ROW_OFFSET_Y
  const out: MenuRect[] = []
  for (let i = 0; i < 3; i += 1) {
    out.push(rectFromRowSpan(cx, 'center', sy0 + i * PAUSE_SETTINGS_ROW_H, PAUSE_SETTINGS_ROW_H, PAUSE_SETTINGS_PANEL_W - 32))
  }
  return out
}

// 終幕の選択 (src/render.ts drawChoice 参照)。
export function endingChoiceRects(): MenuRect[] {
  const oy = ARENA.height * ENDING_CHOICE_Y_FRAC
  const out: MenuRect[] = []
  for (let i = 0; i < 2; i += 1) {
    const ox = cx + (i === 0 ? -ENDING_CHOICE_OFFSET_X : ENDING_CHOICE_OFFSET_X)
    out.push(rectFromBaseline(ox, 'center', oy, ENDING_CHOICE_FONT_PX, 180))
  }
  return out
}

// 点を含む項目の index。 どれにも当たらなければ -1。
export function hitIndex(rects: MenuRect[], x: number, y: number): number {
  for (let i = 0; i < rects.length; i += 1) {
    const r = rects[i]!
    if (x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h) return i
  }
  return -1
}
